import {
  AuditOutlined,
  BarChartOutlined,
  BellOutlined,
  BookOutlined,
  ContactsOutlined,
  CarOutlined,
  DatabaseOutlined,
  DesktopOutlined,
  EnvironmentOutlined,
  FrownOutlined,
  PhoneOutlined,
  SettingOutlined,
  TeamOutlined,
  ToolOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import type { ComponentType } from 'react';

import type { Role } from '@/features/auth/authContext';
import { DICTIONARY_CATALOG, type DictionaryGroup } from '@/features/dictionaries/catalog';
import { ru } from '@/i18n/ru';

export interface NavChild {
  path: string;
  label: string;
  group: DictionaryGroup;
}

export interface NavItem {
  path: string;
  label: string;
  roles: readonly Role[];
  icon: ComponentType;
  /** Plan stage that delivers the section (placeholder until then). */
  stage: number;
  children?: readonly NavChild[];
  /** Reachable but not listed: /calls holds a call's card, opened from the journals. */
  hidden?: boolean;
}

// The vendor (superadmin) sees what an admin sees, and its own section on top (STAGE_6 6.7).
const STAFF: readonly Role[] = ['admin', 'superadmin', 'operator'];
const ADMIN: readonly Role[] = ['admin', 'superadmin'];
const VENDOR: readonly Role[] = ['superadmin'];

// Web sections by role (STAGE_1 1.9). Doctors use the PWA at /doctor instead. The
// operator's menu is the desk, the journals, patients, shifts (STAGE_4 4.7) and their
// reports (STAGE_7 7.6); the desk comes first, so it is where they land after logging in.
export const NAV_ITEMS: readonly NavItem[] = [
  { path: '/dispatch', label: ru.nav.dispatch, roles: STAFF, icon: DesktopOutlined, stage: 4 },
  { path: '/journals', label: ru.nav.journals, roles: STAFF, icon: BookOutlined, stage: 4 },
  { path: '/patients', label: ru.nav.patients, roles: STAFF, icon: TeamOutlined, stage: 1 },
  { path: '/shifts', label: ru.nav.shifts, roles: STAFF, icon: CarOutlined, stage: 2 },
  // A call's card and the stage 2 intake page; the list is the intake journal now.
  {
    path: '/calls',
    label: ru.nav.calls,
    roles: STAFF,
    icon: PhoneOutlined,
    stage: 2,
    hidden: true,
  },
  // The stage 3 fleet map stays as the admin's debugging screen; the desk has its own.
  {
    path: '/fleet-map',
    label: ru.nav.fleetMap,
    roles: ADMIN,
    icon: EnvironmentOutlined,
    stage: 3,
  },
  {
    path: '/dictionaries',
    label: ru.nav.dictionaries,
    roles: ADMIN,
    icon: DatabaseOutlined,
    stage: 1,
    children: DICTIONARY_CATALOG.map(({ slug, label, group }) => ({
      path: `/dictionaries/${slug}`,
      label,
      group,
    })),
  },
  {
    path: '/notifications',
    label: ru.nav.notifications,
    roles: ADMIN,
    icon: BellOutlined,
    stage: 6,
  },
  {
    path: '/incidents',
    label: ru.nav.incidents,
    roles: ADMIN,
    icon: WarningOutlined,
    stage: 6,
  },
  {
    path: '/complaints',
    label: ru.nav.complaints,
    roles: ADMIN,
    icon: FrownOutlined,
    stage: 6,
  },
  {
    path: '/audit',
    label: ru.nav.audit,
    roles: ADMIN,
    icon: AuditOutlined,
    stage: 6,
  },
  // Referrers and their fees (services.referrals): the admin's alone.
  {
    path: '/referrers',
    label: ru.nav.referrers,
    roles: ADMIN,
    icon: ContactsOutlined,
    stage: 7,
  },
  // The operator's are calls, the queue and the cash of their own shift (STAGE_7 7.6):
  // the server lists what each role may run.
  { path: '/reports', label: ru.nav.reports, roles: STAFF, icon: BarChartOutlined, stage: 7 },
  { path: '/settings', label: ru.nav.settings, roles: ADMIN, icon: SettingOutlined, stage: 1 },
  { path: '/vendor', label: ru.nav.vendor, roles: VENDOR, icon: ToolOutlined, stage: 6 },
];

export const DOCTOR_HOME = '/doctor';

/** Every section the role may open, listed or not. */
export function navItemsFor(role: Role): NavItem[] {
  return NAV_ITEMS.filter((item) => item.roles.includes(role));
}

/** The sections the role sees in its menu. */
export function menuItemsFor(role: Role): NavItem[] {
  return navItemsFor(role).filter((item) => !item.hidden);
}

// Where a signed-in user whose role opens no section lands (a role this build does not
// know, say, after the server was updated before the browser): a screen with "Log out",
// never a redirect back to "/" and a blank page.
export const NO_ACCESS_PATH = '/no-access';

/** Where a user lands after login or on "/". */
export function homePath(role: Role): string {
  if (role === 'doctor') return DOCTOR_HOME;
  return navItemsFor(role)[0]?.path ?? NO_ACCESS_PATH;
}

/** Whether `path` (a local pathname) is open to `role`. */
export function canOpen(role: Role, path: string): boolean {
  if (path === DOCTOR_HOME || path.startsWith(`${DOCTOR_HOME}/`)) return role === 'doctor';
  if (role === 'doctor') return false;
  return navItemsFor(role).some(
    (item) => path === item.path || path.startsWith(`${item.path}/`),
  );
}
